import React, { useEffect, useState } from "react";
import { Calendar, MessageCircle, Wrench } from "lucide-react";
import { logIntent } from "@/lib/api";

/**
 * Sticky bottom CTA bar — appears on every page after the user scrolls a bit.
 * Three buttons: Book a Calendly appointment, open the AI chatbot, jump to tools.
 */
const CALENDLY_URL = "https://calendly.com/zayn514/30min";

export default function StickyCTABar() {
    const [visible, setVisible] = useState(false);
    const [footerInView, setFooterInView] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 320);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Hide the sticky bar whenever the footer is in the viewport so it never
    // covers the Admin link (or any other footer content) at the bottom of the page.
    useEffect(() => {
        const footer = document.querySelector("footer");
        if (!footer || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(
            ([entry]) => setFooterInView(entry.isIntersecting),
            { rootMargin: "0px 0px -40px 0px", threshold: 0 }
        );
        io.observe(footer);
        return () => io.disconnect();
    }, []);

    const showBar = visible && !footerInView;

    const openChat = () => {
        // ChatWidget exposes a click target via [data-testid=chat-bubble]
        const pill = document.querySelector("[data-testid=chat-bubble]");
        if (pill) pill.click();
    };

    const jumpToTools = (e) => {
        e.preventDefault();
        const target =
            document.getElementById("rate-compare-section") ||
            document.getElementById("mortgage-cta-section") ||
            document.getElementById("mortgage-calculators-section");
        if (target) {
            target.scrollIntoView({ behavior: "smooth" });
        } else {
            window.location.href = "/#rate-compare-section";
        }
    };

    return (
        <div
            data-testid="sticky-cta-bar"
            aria-hidden={!showBar}
            className={`fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ${
                showBar ? "translate-y-0" : "translate-y-full pointer-events-none"
            }`}
        >
            <div className="mx-auto max-w-3xl m-3 md:m-4 bg-brand-primary text-white rounded-2xl shadow-[0_24px_48px_-15px_rgba(14,58,117,0.55)] border border-brand-primaryHover backdrop-blur">
                <div className="grid grid-cols-3 divide-x divide-white/15">
                    <a
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logIntent("calendly")}
                        data-testid="sticky-cta-book"
                        className="flex flex-col items-center justify-center gap-1 py-3 md:py-4 hover:bg-brand-primaryHover transition-colors text-center"
                    >
                        <Calendar size={18} className="text-brand-mint" />
                        <span className="text-[10px] md:text-xs uppercase tracking-[0.16em] font-bold">
                            Book appointment
                        </span>
                    </a>
                    <button
                        onClick={openChat}
                        data-testid="sticky-cta-chat"
                        className="flex flex-col items-center justify-center gap-1 py-3 md:py-4 hover:bg-brand-primaryHover transition-colors text-center"
                    >
                        <MessageCircle size={18} className="text-brand-mint" />
                        <span className="text-[10px] md:text-xs uppercase tracking-[0.16em] font-bold">
                            Get started
                        </span>
                    </button>
                    <a
                        href="/#rate-compare-section"
                        onClick={jumpToTools}
                        data-testid="sticky-cta-tools"
                        className="flex flex-col items-center justify-center gap-1 py-3 md:py-4 hover:bg-brand-primaryHover transition-colors text-center"
                    >
                        <Wrench size={18} className="text-brand-mint" />
                        <span className="text-[10px] md:text-xs uppercase tracking-[0.16em] font-bold">
                            Helpful tools
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
}
